import React, { useEffect, useRef, useState } from 'react';
import { Field, Image, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { EmblaOptionsType } from 'embla-carousel';

import { resolveColorProperty } from '@gn-audio/jss-components';

import { JabraSlider, JabraSliderRef } from 'common/components/JabraSlider/JabraSlider';
import { PillsSliderNavigation } from 'common/components/JabraSlider/PillsSliderNavigation';
import { mapSharedPropertiesToFields } from 'common/getters';
import { SitecoreFieldTypes, SitecoreMediaItem } from 'common/types/sitecore';
import { ComponentProps } from 'lib/component-props';

interface GallerySliderSpotProps extends ComponentProps {
  fields: SitecoreFieldTypes;
  props: {
    [name: string]: string;
  };
}

type GallerySliderSpotModel = {
  BackgroundColor: Field<string>;
  FullScreenEnabled: Field<boolean>;
  Images: SitecoreMediaItem[];
};

const GallerySliderSpot: React.FC<GallerySliderSpotProps> = ({ fields }) => {
  const gallerySliderSpot = mapSharedPropertiesToFields<GallerySliderSpotModel>(fields);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [startIndex] = useState<number>(0);

  const bgColor = gallerySliderSpot.BackgroundColor?.value;
  const isBackgroundColorGradient = (backgroundColor: string | undefined) => {
    return backgroundColor?.startsWith('linear-gradient');
  };
  const spotBackgroundColor = isBackgroundColorGradient(bgColor)
    ? bgColor
    : resolveColorProperty(bgColor) ?? 'transparent';
  const spotBackgroundStyle = {
    background: `${spotBackgroundColor}`,
  };
  const jabraSlider = useRef<JabraSliderRef>(null);

  const sliderSettings: EmblaOptionsType = {
    slidesToScroll: 1,
    loop: true,
  };

  // Keyboard navigation handler
  const handleKeyDown = (event: KeyboardEvent) => {
    if (jabraSlider.current) {
      if (event.key === 'ArrowLeft') {
        jabraSlider.current.prev();
      } else if (event.key === 'ArrowRight') {
        jabraSlider.current.next();
      }
    }
  };

  const handleMoveToSlide = (index: number) => {
    jabraSlider.current?.goTo(index);
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div
      className={classNames(
        'container-fluid flex w-full flex-col outline-none',
        !gallerySliderSpot.FullScreenEnabled.value && 'px-4 md:px-6 lg:px-[6rem]'
      )}
      style={{ background: spotBackgroundStyle.background }}
    >
      <JabraSlider
        mainClassName="relative w-full focus:outline-none"
        startIndex={startIndex}
        slideClassName="basis-full"
        sliderSettings={sliderSettings}
        ref={jabraSlider}
        onChange={setActiveIndex}
        showSlideArrows={false}
      >
        {gallerySliderSpot.Images.map((image) => {
          const { url, fields } = image;

          return (
            <div key={url} className="flex w-full flex-col gap-11">
              <figure
                className={classNames(
                  'max-h-[80vh] w-full overflow-hidden',
                  !gallerySliderSpot.FullScreenEnabled.value && 'rounded-3xl'
                )}
              >
                <Image
                  className="h-full w-full object-cover"
                  alt={fields.Alt?.value}
                  field={{
                    src: url,
                  }}
                  width="100%"
                  height="100%"
                  as
                />
              </figure>
            </div>
          );
        })}
      </JabraSlider>
      <div className="mt-4 flex w-full justify-center">
        <PillsSliderNavigation
          activeIndex={activeIndex}
          total={gallerySliderSpot.Images.length}
          buttonVariant="tertiary"
          sliderButtonPosition="center"
          goTo={handleMoveToSlide}
        />
      </div>
    </div>
  );
};

export default withDatasourceCheck()(GallerySliderSpot);
